import { useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store';
import {
  clearPendingCallLogUpdate,
  addProcessedSessionId,
} from '../../lib/redux/sheets/slice';
import { useSheetsWriteQueue, ValueInputOption } from './useSheetsWriteQueue';
import { useEnsureTrellusColumns } from './useEnsureTrellusColumns';

enum TrellusColumn {
  DO_NOT_CALL = 'Do not call again (Trellus)',
  LAST_CALL_ATTEMPT = 'TRELLUS_Last_Call_Attempt',
  LAST_CALL_OUTCOME = 'TRELLUS_Last_Call_Outcome',
  LAST_TIME_HUMAN = 'TRELLUS_Last_Time_Human',
  TOTAL_CALL_ATTEMPTS = 'TRELLUS_Total_Call_Attempts',
  CALL_NOTES = 'TRELLUS_Call_Notes',
  ALL_PREVIOUS_CALLS = 'TRELLUS_All_Previous_Calls',
  ALL_DISPOSITIONS = 'TRELLUS_All_Dispositions'
}

enum CallDisposition {
  NO_ANSWER = 'NO_ANSWER',
  LEFT_VOICEMAIL = 'LEFT_VOICEMAIL',
  WRONG_NUMBER = 'WRONG_NUMBER'
}

enum CallLogParams {
  CALL_INFO = 'CALL_INFO',
  CONTACT_INFO = 'CONTACT_INFO'
}

enum CallInfoParams {
  SESSION = 'SESSION',
  OUTCOME = 'OUTCOME',
  OUTCOMES_PLATFORM_SPECIFIC = 'OUTCOMES_PLATFORM_SPECIFIC'
}

enum ContactInfoParams {
  CONTACT_ID = 'CONTACT_ID'
}

enum SessionParams {
  SESSION_ID = 'session_id'
}

enum OutcomesPlatformSpecificParams {
  SUMMARY = 'SUMMARY',
  DISPOSITION = 'DISPOSITION'
}

const NO_HUMAN_DISPOSITIONS = [CallDisposition.NO_ANSWER, CallDisposition.LEFT_VOICEMAIL, CallDisposition.WRONG_NUMBER];

export function useCallLogHandler() {
  const dispatch = useDispatch();
  const { queueWriteByColumn, queueAtomicUpdateByColumn, getSheetName, invalidateColumnCache } = useSheetsWriteQueue();
  const { ensureTrellusColumns } = useEnsureTrellusColumns();
  const auth = useSelector((state: RootState) => state.googleSheets.auth);
  const windowMessages = useSelector((state: RootState) => state.googleSheets.windowMessages);
  const spreadsheetData = useSelector((state: RootState) => state.googleSheets.spreadsheetData);

  const pendingCallLogUpdate = windowMessages.pendingCallLogUpdate;
  const tokenConfig = auth.tokenConfig;
  const spreadsheetAccess = spreadsheetData.spreadsheetAccess;
  const processedSessionIds = Array.isArray(spreadsheetData.callLogTracking?.processedSessionIds)
    ? spreadsheetData.callLogTracking.processedSessionIds
    : [];

  const applyCallLogUpdate = useCallback(async (spreadsheetId: string, callLogData: any, sheetGid?: string) => {
    const updateId = `${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;

    try {
      const sessionId = callLogData[CallLogParams.CALL_INFO]?.[CallInfoParams.SESSION]?.[SessionParams.SESSION_ID];
      if (!sessionId) {
        return { success: false, error: 'No session_id' };
      }


      if (processedSessionIds.includes(sessionId)) {
        return { success: true }; // Already processed
      }
      // Ensure columns exist with cache invalidation callback
      const columnsEnsured = await ensureTrellusColumns(spreadsheetId, sheetGid, (spreadsheetId, sheetName) => {
        invalidateColumnCache(spreadsheetId, sheetName);
      });
      if (!columnsEnsured) {
        return { success: false, error: 'Failed to ensure columns' };
      }

      dispatch(addProcessedSessionId({ sessionId, spreadsheetId }));

      // Get sheet name with caching
      const sheetName = await getSheetName(spreadsheetId, sheetGid);

      const contactId = callLogData[CallLogParams.CONTACT_INFO]?.[ContactInfoParams.CONTACT_ID];
      if (contactId === undefined) {
        return { success: false, error: 'No CONTACT_ID' };
      }

      const rowNumber = contactId + 2; // +1 for header, +1 for 1-based
      const currentTime = new Date().toISOString();
      const outcome = callLogData[CallLogParams.CALL_INFO]?.[CallInfoParams.OUTCOME];
      const humanConnected = outcome && !NO_HUMAN_DISPOSITIONS.includes(outcome);
      const callSummary = callLogData[CallLogParams.CALL_INFO]?.[CallInfoParams.OUTCOMES_PLATFORM_SPECIFIC]?.[OutcomesPlatformSpecificParams.SUMMARY];
      const disposition = callLogData[CallLogParams.CALL_INFO]?.[CallInfoParams.OUTCOMES_PLATFORM_SPECIFIC]?.[OutcomesPlatformSpecificParams.DISPOSITION];


      // Simple helper to queue a cell update by column name
      const updateCell = (columnName: string, value: string) => {
        queueWriteByColumn(spreadsheetId, sheetName, columnName, rowNumber, value);
      };

      // Simple helper to queue an atomic append by column name
      const appendToCell = (columnName: string, newValue: string, separator = '\n') => {
        queueAtomicUpdateByColumn(spreadsheetId, sheetName, columnName, rowNumber, (currentValue) => {
          return currentValue ? `${currentValue}${separator}${newValue}` : newValue;
        });
      };

      // Simple helper to queue an atomic increment by column name
      const incrementCell = (columnName: string) => {
        queueAtomicUpdateByColumn(spreadsheetId, sheetName, columnName, rowNumber, (currentValue) => {
          const currentNumber = parseInt(currentValue) || 0;
          return (currentNumber + 1).toString();
        }, { valueInputOption: ValueInputOption.USER_ENTERED });
      };

      // Queue all updates - operations will execute in order

      updateCell(TrellusColumn.LAST_CALL_ATTEMPT, currentTime);
      if (humanConnected) updateCell(TrellusColumn.LAST_TIME_HUMAN, currentTime);
      incrementCell(TrellusColumn.TOTAL_CALL_ATTEMPTS);
      if (callSummary?.trim()) appendToCell(TrellusColumn.CALL_NOTES, callSummary);
      if (disposition) updateCell(TrellusColumn.LAST_CALL_OUTCOME, disposition);
      if (sessionId) appendToCell(TrellusColumn.ALL_PREVIOUS_CALLS, `https://app.trellus.ai/transcripts?id=${sessionId}`, ', ');
      if (disposition) {
        const now = new Date();
        const timestamp = `${disposition} - ${now.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' })} ${now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}`;
        appendToCell(TrellusColumn.ALL_DISPOSITIONS, timestamp, ', ');
      }

      return { success: true };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }, [queueWriteByColumn, queueAtomicUpdateByColumn, ensureTrellusColumns, processedSessionIds, dispatch]);

  const handleCallLogUpdateRequest = useCallback(async (spreadsheetId: string, updateData: any, sheetGid?: string) => {
    const requestId = `${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;

    try {
      if (!tokenConfig) {
        return;
      }
      if (!spreadsheetAccess.includes(spreadsheetId)) {
        return;
      }

      const result = await applyCallLogUpdate(spreadsheetId, updateData, sheetGid);
    } catch (error) {
    } finally {
      dispatch(clearPendingCallLogUpdate());
    }
  }, [tokenConfig, spreadsheetAccess, dispatch, applyCallLogUpdate]);

  useEffect(() => {
    if (!pendingCallLogUpdate || !tokenConfig) {
      if (pendingCallLogUpdate && !tokenConfig) {
      }
      return;
    }


    handleCallLogUpdateRequest(pendingCallLogUpdate.spreadsheetId, pendingCallLogUpdate.data, pendingCallLogUpdate.sheetGid);
  }, [pendingCallLogUpdate, tokenConfig, handleCallLogUpdateRequest]);
}